import { Button, Card, CardBody, CardHeader, Select, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LABEL_SPECS, labelKindSchema, type LabelKind } from '@warehouse/shared';
import { Printer as PrinterIcon } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getTemplateSample,
  listPrinters,
  listTemplates,
  previewPng,
  previewZpl,
  printLabel,
} from '../api';
import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastSuccess } from '../../../lib/notify';

const KINDS: LabelKind[] = ['pallet_label', 'bin_label', 'article_label', 'test_label'];
const DPI_OPTIONS = [203, 300, 600];

/**
 * Label template preview.
 *
 * The templates live on the SERVER (ARCHITECTURE §8.4), so this page cannot
 * edit them — it renders them. Both formats come from the same endpoint and
 * therefore from the same ZPL the print queue would send: the PNG is that ZPL
 * run through the self-hosted rasterizer, and the "ZPL-källa" tab is the source
 * itself. Previewing something other than what prints would be worse than not
 * previewing at all.
 */
export function TemplatesPage(): ReactNode {
  const { t } = useTranslation(['devicesPrinting', 'common']);
  const [kind, setKind] = useState<LabelKind>('pallet_label');
  const [dpi, setDpi] = useState(203);
  const [scale, setScale] = useState(2);
  const [printerId, setPrinterId] = useState('');
  const [pngUrl, setPngUrl] = useState<string | null>(null);

  const templates = useQuery({ queryKey: ['print-templates'], queryFn: listTemplates });
  const sample = useQuery({
    queryKey: ['print-template-sample', kind],
    queryFn: () => getTemplateSample(kind),
  });
  const printers = useQuery({
    queryKey: ['printers', 'options'],
    queryFn: () => listPrinters({ page: 1, pageSize: 200 }),
  });

  const zpl = useQuery({
    queryKey: ['print-template-zpl', kind, dpi, sample.data?.payload],
    queryFn: () => previewZpl(kind, sample.data?.payload, dpi),
    enabled: Boolean(sample.data),
  });

  // The PNG is binary, so it lives behind an object URL that must be revoked —
  // a preview page that leaks a blob per keystroke is a slow memory leak on a
  // screen people leave open.
  useEffect(() => {
    if (!sample.data) return;
    let revoked = false;
    let current: string | null = null;
    void previewPng(kind, sample.data.payload, { dpi, scale })
      .then((url) => {
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        current = url;
        setPngUrl((previous) => {
          if (previous) URL.revokeObjectURL(previous);
          return url;
        });
      })
      .catch((error: unknown) => {
        toastError(error);
      });
    return () => {
      revoked = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [kind, dpi, scale, sample.data]);

  const print = useMutation({
    mutationFn: () =>
      printLabel({ kind, payload: sample.data?.payload, printerId, copies: 1 }),
    onSuccess: () => {
      const name = printers.data?.items.find((p) => p.id === printerId)?.name ?? '';
      toastSuccess(t('devicesPrinting:print.queued', { name }));
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const spec = useMemo(() => LABEL_SPECS[kind], [kind]);
  const labelPrinters = (printers.data?.items ?? []).filter((p) => p.kind === 'label' && p.isActive);

  return (
    <>
      <PageHeader
        title={t('devicesPrinting:templates.title')}
        subtitle={t('devicesPrinting:templates.subtitle')}
      />

      <div className="grid gap-5 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <h2 className="font-display text-base font-semibold text-ink">
              {t('devicesPrinting:templates.kind')}
            </h2>
          </CardHeader>
          <CardBody className="flex flex-col gap-4">
            <Select
              label={t('devicesPrinting:templates.kind')}
              value={kind}
              options={KINDS.map((k) => ({ value: k, label: t(`devicesPrinting:templates.kinds.${k}`) }))}
              onChange={(event) => {
                const parsed = labelKindSchema.safeParse(event.target.value);
                if (parsed.success) setKind(parsed.data);
              }}
            />
            <p className="text-sm text-ink-muted">
              {t(`devicesPrinting:templates.descriptions.${kind}`)}
            </p>
            <p className="font-mono text-xs text-ink-muted">
              {t('devicesPrinting:templates.size', { width: spec.widthMm, height: spec.heightMm })}
            </p>

            <Select
              label={t('devicesPrinting:templates.dpi')}
              value={String(dpi)}
              options={DPI_OPTIONS.map((value) => ({ value: String(value), label: `${value} dpi` }))}
              onChange={(event) => {
                setDpi(Number(event.target.value));
              }}
            />
            <Select
              label={t('devicesPrinting:templates.scale')}
              value={String(scale)}
              options={[1, 2, 3].map((value) => ({ value: String(value), label: `${value}×` }))}
              onChange={(event) => {
                setScale(Number(event.target.value));
              }}
            />

            <div className="flex flex-col gap-2 border-t border-surface-border pt-4">
              <Select
                label={t('devicesPrinting:print.printer')}
                placeholder={t('devicesPrinting:print.choosePrinter')}
                value={printerId}
                options={labelPrinters.map((printer) => ({ value: printer.id, label: printer.name }))}
                onChange={(event) => {
                  setPrinterId(event.target.value);
                }}
              />
              {labelPrinters.length === 0 ? (
                <p className="text-xs text-ink-muted">{t('devicesPrinting:print.noPrinters')}</p>
              ) : null}
              <Button
                icon={<PrinterIcon />}
                disabled={!printerId}
                loading={print.isPending}
                onClick={() => {
                  print.mutate();
                }}
              >
                {t('devicesPrinting:templates.printPreview')}
              </Button>
            </div>
          </CardBody>
        </Card>

        <div className="flex flex-col gap-5">
          <Card>
            <CardHeader>
              <h2 className="font-display text-base font-semibold text-ink">
                {t('devicesPrinting:templates.preview')}
              </h2>
            </CardHeader>
            <CardBody className="flex flex-col items-center gap-3">
              {pngUrl ? (
                <img
                  src={pngUrl}
                  alt={t('devicesPrinting:templates.previewAlt', {
                    kind: t(`devicesPrinting:templates.kinds.${kind}`),
                  })}
                  data-testid="label-preview"
                  className="max-w-full border border-surface-border bg-white"
                />
              ) : (
                <p className="text-sm text-ink-muted">{t('common:common.loading')}</p>
              )}
              {kind === 'pallet_label' ? (
                <p className="text-xs text-ink-muted">{t('devicesPrinting:templates.note2d')}</p>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="font-display text-base font-semibold text-ink">
                {t('devicesPrinting:templates.zplSource')}
              </h2>
            </CardHeader>
            <CardBody>
              <Surface variant="subtle" className="max-h-72 overflow-auto p-3 scrollbar-thin">
                <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-ink">
                  {zpl.data?.zpl ?? ''}
                </pre>
              </Surface>
            </CardBody>
          </Card>

          {templates.data ? (
            <p className="text-xs text-ink-muted">
              {templates.data.items.map((info) => info.kind).join(' · ')}
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
}
